import { useEffect } from "react";
import { X, ShoppingBag, Plus, Minus, Trash2, Lock, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/shopify";
import { toast } from "sonner";

export default function CartDrawer() {
    const {
        items,
        itemCount,
        subtotal,
        currency,
        open,
        setOpen,
        updateQuantity,
        removeItem,
        checkout,
        checkingOut,
    } = useCart();

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const onCheckout = async () => {
        try {
            await checkout();
        } catch (err) {
            toast("Could not start checkout", { description: err.message || "Try again" });
        }
    };

    return (
        <div
            className={`fixed inset-0 z-[80] ${open ? "pointer-events-auto" : "pointer-events-none"}`}
            aria-hidden={!open}
            data-testid="cart-drawer"
        >
            {/* Backdrop */}
            <div
                className={`absolute inset-0 bg-black/45 backdrop-blur-sm transition-opacity duration-300 ${
                    open ? "opacity-100" : "opacity-0"
                }`}
                onClick={() => setOpen(false)}
            />

            {/* Drawer */}
            <aside
                className={`absolute right-0 top-0 h-full w-full sm:w-[420px] bg-bb-cream text-bb-text shadow-2xl flex flex-col transition-transform duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] ${
                    open ? "translate-x-0" : "translate-x-full"
                }`}
                data-testid="cart-drawer-aside"
            >
                {/* Header */}
                <header className="flex items-center justify-between px-6 h-20 bg-bb-black text-white">
                    <div className="flex items-center gap-3">
                        <ShoppingBag size={20} className="text-bb-pink-script" />
                        <h2 className="font-serif-display tracking-[0.22em] uppercase text-base">
                            Your Bag
                        </h2>
                        {itemCount > 0 && (
                            <span className="bg-bb-pink-script text-white text-[0.65rem] px-2 py-0.5 rounded-full font-semibold">
                                {itemCount}
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        aria-label="Close cart"
                        data-testid="cart-close"
                        className="text-white hover:text-bb-pink-script transition-colors"
                    >
                        <X size={22} />
                    </button>
                </header>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-6">
                    {items.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center" data-testid="cart-empty">
                            <div className="w-16 h-16 rounded-full bg-bb-blush-2 flex items-center justify-center">
                                <ShoppingBag size={26} className="text-bb-rose-gold" />
                            </div>
                            <p className="mt-4 font-script text-2xl text-bb-pink-script">
                                Your bag is empty
                            </p>
                            <p className="mt-2 text-sm text-bb-text-soft max-w-[260px]">
                                Add a blessing or two — we'll take care of the rest.
                            </p>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="btn-blush mt-6"
                            >
                                Continue Shopping
                            </button>
                        </div>
                    ) : (
                        <ul className="space-y-5">
                            {items.map((item) => (
                                <li
                                    key={item.variantId}
                                    className="flex gap-3 pb-5 border-b border-bb-warm-brown/15"
                                    data-testid={`cart-item-${item.handle}`}
                                >
                                    <div className="w-20 h-20 rounded-md overflow-hidden bg-white border border-bb-warm-brown/20 shrink-0">
                                        {item.image ? (
                                            <img
                                                src={item.image}
                                                alt={item.title}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-bb-text-soft">
                                                <ShoppingBag size={18} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-serif-display text-sm text-bb-text leading-snug line-clamp-2">
                                            {item.title}
                                        </h3>
                                        <p className="mt-1 font-serif-display text-bb-pink-script text-sm">
                                            {formatPrice(item.price)}
                                        </p>
                                        <div className="mt-2 flex items-center justify-between">
                                            <div className="inline-flex items-center border border-bb-warm-brown/30 rounded-full">
                                                <button
                                                    type="button"
                                                    aria-label="Decrease quantity"
                                                    data-testid={`cart-dec-${item.handle}`}
                                                    onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                                                    className="w-7 h-7 flex items-center justify-center text-bb-text hover:text-bb-pink-script"
                                                >
                                                    <Minus size={12} />
                                                </button>
                                                <span className="w-6 text-center text-sm font-semibold">
                                                    {item.quantity}
                                                </span>
                                                <button
                                                    type="button"
                                                    aria-label="Increase quantity"
                                                    data-testid={`cart-inc-${item.handle}`}
                                                    onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                                                    className="w-7 h-7 flex items-center justify-center text-bb-text hover:text-bb-pink-script"
                                                >
                                                    <Plus size={12} />
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                aria-label="Remove item"
                                                data-testid={`cart-remove-${item.handle}`}
                                                onClick={() => removeItem(item.variantId)}
                                                className="text-bb-text-soft hover:text-bb-pink-script transition-colors"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Footer */}
                {items.length > 0 && (
                    <footer className="border-t border-bb-warm-brown/20 px-6 pt-5 pb-6 bg-white/70">
                        <div className="flex items-center justify-between mb-4">
                            <span className="font-serif-display tracking-[0.18em] uppercase text-sm text-bb-text">
                                Subtotal
                            </span>
                            <span
                                className="font-serif-display text-xl text-bb-pink-script"
                                data-testid="cart-subtotal"
                            >
                                {formatPrice({ amount: subtotal, currency })}
                            </span>
                        </div>
                        <p className="text-[0.7rem] text-bb-text-soft mb-4">
                            Shipping, taxes & discounts calculated at secure checkout.
                        </p>
                        <button
                            type="button"
                            onClick={onCheckout}
                            disabled={checkingOut}
                            data-testid="cart-checkout"
                            className="btn-black btn-shine w-full disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {checkingOut ? "Preparing checkout…" : "Secure Checkout"}
                            {!checkingOut && <ArrowRight size={14} />}
                        </button>
                        <div className="mt-3 flex items-center justify-center gap-1.5 text-[0.65rem] text-bb-text-soft tracking-[0.18em] uppercase">
                            <Lock size={10} /> Secured by Shopify
                        </div>
                    </footer>
                )}
            </aside>
        </div>
    );
}
